import React from "react";
import { useTranslation } from "react-i18next";
import Container from "../Components/Container";
import TitleSection from "../Components/TitleSection";
import EventSummary from "../Components/EventSummary";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { ArrowLeft, CalendarX } from "@phosphor-icons/react";
import { findCancelReasonByResponsible } from "@constants/cancelReasons";
import useCancelVideoCall from "hooks/api/appointments/useCancelVideoCall";
import useGetCancellationReasons from "hooks/api/appointments/useGetCancellationReasons";
import { LoadingButton } from "@mui/lab";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useParams } from "react-router-dom";
import { STEPS } from "../utils";
import { useRescheduling } from "../ReschedulingProvider";

const cancelReasons = [
  ...findCancelReasonByResponsible("client").cancelReasons,
  "Prefer not to say",
];

const cancelReasonTranslationKeys = {
  "Client or Caregiver Sick": "clientOrCaregiverSick",
  "Internet / Tech Issues": "internetTechIssues",
  "Personal Conflict": "personalConflict",
  "Last Minute Medical Appointment": "lastMinuteMedicalAppointment",
  "Client Behavior": "clientBehavior",
  "Scheduled Vacation": "scheduledVacation",
  Bereavement: "bereavement",
  "Prefer not to say": "preferNotToSay",
};

const catalogReasonTranslationKeys = {
  "client-caregiver-sick": "clientOrCaregiverSick",
  "client-internet-tech-issues": "internetTechIssues",
  "client-personal-conflict": "personalConflict",
  "client-last-minute-medical-appointment": "lastMinuteMedicalAppointment",
  "client-client-behavior": "clientBehavior",
  "client-scheduled-vacation": "scheduledVacation",
  "client-bereavement": "bereavement",
};

const getCatalogReasonLabel = (reason, t) => {
  const translationKey = catalogReasonTranslationKeys[reason.reasonKey];
  return translationKey
    ? t(`rescheduling:cancelReasons.${translationKey}`, { defaultValue: reason.displayLabel })
    : reason.displayLabel;
};

export default function CancelAppointment() {
  const { t } = useTranslation(["rescheduling"]);
  const { setStep, onBack, callInfo } = useRescheduling();
  const { showToast } = useGlobalToast();
  const { id: videoId } = useParams();
  const [cancelReason, setCancelReason] = React.useState("");
  const [cancellationReasonId, setCancellationReasonId] = React.useState("");
  const [screenWidth] = React.useState(window.innerWidth);
  const { cancelVideoCall, isCancelVideoCallLoading } = useCancelVideoCall();
  const {
    cancellationReasons,
    isLoading: isCancellationReasonsLoading,
  } = useGetCancellationReasons();
  const catalogClientReasons = Array.isArray(cancellationReasons)
    ? cancellationReasons.filter((reason) => reason.responsibleForCancellation === "client")
    : [];
  const usesCatalog = catalogClientReasons.length > 0;
  const selectedCatalogReason = catalogClientReasons.find(
    (reason) => String(reason.id) === String(cancellationReasonId)
  );
  const selectedCatalogReasonId = selectedCatalogReason?.id;

  const onChangeCancelReason = (e) => {
    const selectedValue = e.target.value;
    const selectedCatalogReason = catalogClientReasons.find(
      (reason) => String(reason.id) === String(selectedValue)
    );

    if (selectedCatalogReason) {
      setCancellationReasonId(selectedCatalogReason.id);
      setCancelReason(selectedCatalogReason.displayLabel);
      return;
    }

    setCancellationReasonId("");
    setCancelReason(selectedValue);
  };

  return (
    <Container showGoHome>
      <TitleSection
        title={t("rescheduling:cancelAppointment.title")}
        subtitle={t("rescheduling:cancelAppointment.subtitle")}
      />
      <EventSummary eventDate={callInfo?.scheduled_date} />
      <Stack gap={7}>
        <FormControl disabled={isCancellationReasonsLoading}>
          <FormLabel>{t("rescheduling:cancelAppointment.provideReason")}</FormLabel>
          <RadioGroup
            aria-label="cancellationReason"
            name="cancellationReason"
            value={usesCatalog ? selectedCatalogReasonId ?? "" : cancelReason}
            onChange={onChangeCancelReason}
          >
            {usesCatalog
              ? catalogClientReasons.map((reason) => (
                  <FormControlLabel
                    value={reason.id}
                    control={<Radio color="primary" size={screenWidth < 834 ? "large" : "small"} />}
                    label={getCatalogReasonLabel(reason, t)}
                    key={reason.id}
                  />
                ))
              : cancelReasons.map((reason, i) => (
                  <FormControlLabel
                    value={reason}
                    control={<Radio color="primary" size={screenWidth < 834 ? "large" : "small"} />}
                    label={t(`rescheduling:cancelReasons.${cancelReasonTranslationKeys[reason]}`)}
                    key={i}
                  />
                ))}
          </RadioGroup>
        </FormControl>
        <Stack gap={3}>
          <LoadingButton
            disabled={
              isCancellationReasonsLoading ||
              !cancelReason ||
              (usesCatalog && !selectedCatalogReason)
            }
            loading={isCancelVideoCallLoading}
            size="large"
            color="error"
            startIcon={<CalendarX />}
            onClick={() =>
              cancelVideoCall(
                {
                  id: videoId,
                  message: cancelReason,
                  responsibleForCancellation: "client",
                  ...(selectedCatalogReasonId !== undefined
                    ? { cancellationReasonId: selectedCatalogReasonId }
                    : {}),
                  //   clinicianUserId: videoChatInfo.clinician_user_id,
                  cancelAllInstances: false,
                },
                {
                  onSuccess: () => {
                    showToast({ message: t("rescheduling:cancelAppointment.successToast") });
                    setStep(STEPS.cancellationConfirmation);
                  },
                }
              )
            }
          >
            {t("rescheduling:common.cancelAppointment")}
          </LoadingButton>
          <Button
            size="large"
            variant="text"
            color="secondary"
            onClick={onBack}
            startIcon={<ArrowLeft />}
          >
            {t("rescheduling:common.back")}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
